import { useRef, useState, useEffect } from 'react';
import { useI18n } from '../i18n/I18nContext';
import { projects } from '../data/projects';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function ProjectsSection() {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const projectCards = containerRef.current?.querySelectorAll('.project-card');
      if (projectCards) {
        gsap.fromTo(
          projectCards,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top 75%',
            },
          }
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={containerRef}
      id="projects"
      className="projects-section"
      style={{
        minHeight: '100vh',
        padding: '6rem 2rem',
        position: 'relative',
      }}
      aria-labelledby="projects-title"
    >
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <header className="section-header" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 id="projects-title" className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 700,
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, var(--fg-primary), var(--accent-warm))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {t('projects.title')}
          </h2>
          <p className="section-subtitle" style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            color: 'var(--fg-secondary)',
            maxWidth: '600px',
            margin: '0 auto',
          }}>
            {t('projects.subtitle')}
          </p>
        </header>

        <div className="projects-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem',
        }}>
          {projects.map((project, index) => (
            <article
              key={project.id}
              className="project-card"
              style={{
                borderRadius: '20px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                position: 'relative',
                minHeight: '480px',
                backgroundImage: `url(${project.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'top center',
                backgroundRepeat: 'no-repeat',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
                transition: 'transform 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Overlay oscuro para legibilidad del texto — gradiente de abajo hacia arriba */}
              <div
                className="project-gradient"
                style={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(180deg, rgba(10,10,15,0.15) 0%, rgba(10,10,15,0.45) 30%, rgba(10,10,15,0.92) 68%, rgba(10,10,15,0.98) 100%)',
                  zIndex: 1,
                  transition: 'opacity 0.4s ease',
                }}
              />
              {/* Velo extra al hover */}
              <div
                className="project-hover-veil"
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0, 212, 255, 0.06)',
                  opacity: hoveredIndex === index ? 1 : 0,
                  transition: 'opacity 0.4s ease',
                  zIndex: 2,
                  pointerEvents: 'none',
                }}
              />

              {/* Contenido informativo sobre el fondo */}
              <div className="project-content" style={{
                position: 'relative',
                zIndex: 3,
                padding: '2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                transform: hoveredIndex === index ? 'translateY(-6px)' : 'translateY(0)',
                transition: 'transform 0.4s ease',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                }}>
                  <span className="project-category" style={{
                    fontSize: '0.7rem',
                    color: 'var(--accent-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    fontWeight: 700,
                    background: 'rgba(10, 10, 15, 0.75)',
                    padding: '0.3rem 0.8rem',
                    borderRadius: '100px',
                    border: '1px solid rgba(0, 212, 255, 0.35)',
                  }}>
                    {project.category}
                  </span>
                  <span style={{
                    fontSize: '0.7rem',
                    color: '#ffffff',
                    background: 'rgba(0, 255, 136, 0.9)',
                    padding: '0.3rem 0.8rem',
                    borderRadius: '100px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}>
                    {project.featured ? t('projects.featured') : ''}
                  </span>
                </div>

                <h3 className="project-title" style={{
                  fontSize: '1.65rem',
                  fontWeight: 700,
                  color: '#ffffff',
                  textShadow: '0 2px 12px rgba(0, 0, 0, 0.9), 0 1px 3px rgba(0,0,0,0.8)',
                  margin: 0,
                  lineHeight: 1.25,
                }}>
                  {project.title}
                </h3>

                <p className="project-description" style={{
                  fontSize: '0.95rem',
                  color: 'rgba(255, 255, 255, 0.92)',
                  lineHeight: 1.65,
                  textShadow: '0 1px 8px rgba(0, 0, 0, 0.9)',
                  margin: 0,
                }}>
                  {project.description}
                </p>

                <div className="project-tags" style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.45rem',
                  marginTop: '0.25rem',
                }}>
                  {project.tags.slice(0, 4).map(tag => (
                    <span
                      key={tag}
                      style={{
                        padding: '0.3rem 0.75rem',
                        background: 'rgba(10, 10, 15, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.22)',
                        borderRadius: '100px',
                        fontSize: '0.68rem',
                        color: '#ffffff',
                        fontWeight: 600,
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                    style={{
                      padding: '0.65rem 1.35rem',
                      background: 'var(--accent-primary)',
                      color: '#0a0a0f',
                      borderRadius: '100px',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 4px 20px rgba(0, 255, 136, 0.35)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(0, 255, 136, 0.5)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 255, 136, 0.35)'; }}
                  >
                    {t('projects.demo')} →
                  </a>
                  {project.repoUrl && (
                    <a
                      href={project.repoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary"
                      style={{
                        padding: '0.65rem 1.35rem',
                        background: 'rgba(10, 10, 15, 0.7)',
                        border: '1px solid var(--accent-secondary)',
                        color: '#ffffff',
                        borderRadius: '100px',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        backdropFilter: 'blur(6px)',
                        transition: 'transform 0.2s ease, background 0.2s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.background = 'rgba(0, 212, 255, 0.15)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = 'rgba(10, 10, 15, 0.7)'; }}
                    >
                      {t('projects.code')} →
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
